import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { PROPERTY_TYPES, DISTRICT_KEYS, AMENITIES_LIST } from '../constants';
import { useTranslation } from 'react-i18next';
import AdminBreadcrumb from '../components/AdminBreadcrumb';
import { clearAllCaches } from '../hooks/useHousingData';
import { getAccessToken, refreshAccessToken } from '../services/authService';

const PROGRESS_STEPS = {
  FOLDER: 'folder',
  LIST: 'list',
  PROPERTY: 'property',
  PROPERTY_FILE: 'propertyFile',
  SECRET_FILE: 'secretFile',
  IMAGES: 'images',
  DONE: 'done',
};

const STEP_ORDER = [
  PROGRESS_STEPS.FOLDER,
  PROGRESS_STEPS.LIST,
  PROGRESS_STEPS.PROPERTY,
  PROGRESS_STEPS.PROPERTY_FILE,
  PROGRESS_STEPS.SECRET_FILE,
  PROGRESS_STEPS.IMAGES,
  PROGRESS_STEPS.DONE,
];

const AdminPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const fileInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    title: '',
    titleVi: '',
    description: '',
    descriptionVi: '',
    address: '',
    addressVi: '',
    displayAddress: '',
    displayAddressVi: '',
    additionalAddress1: '',
    additionalAddress1Vi: '',
    additionalAddress2: '',
    additionalAddress2Vi: '',
    city: 'Ho Chi Minh City',
    cityVi: 'Hồ Chí Minh',
    district: '',
    districtVi: '',
    price: '',
    phone: '',
    email: '',
    available: true,
    shortTermLease: false,
    propertyType: 'Phòng (1 - 2 người)',
    amenities: [],
    nearby: '',
    nearbyVi: '',
    secretInfo: '',
  });

  const [selectedFiles, setSelectedFiles] = useState([]);
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [driveFolderId, setDriveFolderId] = useState('');
  const [accessToken, setAccessToken] = useState('');

  // Progress overlay state
  const [showProgress, setShowProgress] = useState(false);
  const [currentStep, setCurrentStep] = useState('');
  const [progressMessage, setProgressMessage] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [totalUploads, setTotalUploads] = useState(0);

  // Success popup state
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [successPopupMessage, setSuccessPopupMessage] = useState('');

  // Token refresh state
  const [tokenRefreshing, setTokenRefreshing] = useState(false);
  const [tokenStatus, setTokenStatus] = useState('unknown'); // 'unknown' | 'valid' | 'expired'

  // Initialize on mount — get token and load config
  useEffect(() => {
    const token = getAccessToken();
    if (token) {
      setAccessToken(token);
      initializeApp(token);
    }
  }, []);

  const initializeApp = useCallback(async (token) => {
    const folderId = await loadConfig();
    if (folderId) {
      loadRootFoldersWithToken(folderId, token);
    }
  }, []);

  const loadConfig = async () => {
    try {
      const response = await fetch('/api/admin-config');
      if (response.ok) {
        const config = await response.json();
        setDriveFolderId(config.driveFolderId);
        return config.driveFolderId;
      }
    } catch (err) {
      setError(t('admin.errorNoConfig'));
    }
    return null;
  };

  const loadRootFoldersWithToken = async (folderId, token) => {
    if (!token || !folderId) {
      return;
    }

    setLoading(true);
    setError('');
    
    try {
      const response = await fetch(`/api/admin-list-folders?folderId=${encodeURIComponent(folderId)}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || t('common.error'));
      }

      const data = await response.json();
      setFolders(data.folders || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRefreshToken = async () => {
    setTokenRefreshing(true);
    setTokenStatus('unknown');
    setError('');
    
    try {
      const newToken = await refreshAccessToken();
      if (newToken) {
        setAccessToken(newToken);
        setTokenStatus('valid');
        // Re-initialize with new token
        const folderId = driveFolderId || await loadConfig();
        if (folderId) {
          loadRootFoldersWithToken(folderId, newToken);
        }
        setSuccessPopupMessage(t('admin.tokenRefreshSuccess'));
        setShowSuccessPopup(true);
      } else {
        setTokenStatus('expired');
        setError(t('admin.tokenRefreshFailed'));
      }
    } catch (err) {
      setTokenStatus('expired');
      setError(t('admin.tokenRefreshFailed'));
    } finally {
      setTokenRefreshing(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => {
      const updated = {
        ...prev,
        [name]: type === 'checkbox' ? checked : value,
      };
      // Auto-copy VI to EN when EN is empty
      if (name === 'titleVi' && (!prev.title || !prev.title.trim())) {
        updated.title = value;
      } else if (name === 'descriptionVi' && value && (!prev.description || !prev.description.trim())) {
        updated.description = value;
      } else if (name === 'displayAddressVi' && (!prev.displayAddress || !prev.displayAddress.trim())) {
        updated.displayAddress = value;
      } else if (name === 'cityVi' && (!prev.city || !prev.city.trim())) {
        updated.city = value;
      } else if (name === 'nearbyVi' && (!prev.nearby || !prev.nearby.trim())) {
        updated.nearby = value;
      }
      // Auto-copy additional addresses VI to EN
      if (name === 'additionalAddress1Vi' && (!prev.additionalAddress1 || !prev.additionalAddress1.trim())) {
        updated.additionalAddress1 = value;
      }
      if (name === 'additionalAddress2Vi' && (!prev.additionalAddress2 || !prev.additionalAddress2.trim())) {
        updated.additionalAddress2 = value;
      }
      return updated;
    });
  };

  const handleAmenityToggle = (amenityKey) => {
    setFormData(prev => ({
      ...prev,
      amenities: prev.amenities.includes(amenityKey)
        ? prev.amenities.filter(a => a !== amenityKey)
        : [...prev.amenities, amenityKey],
    }));
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    const mediaFiles = files.filter(f => f.type.startsWith('image/') || f.type.startsWith('video/'));
    setSelectedFiles(prev => [...prev, ...mediaFiles]);
    e.target.value = '';
  };

  const removeFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const updateProgress = (step, message) => {
    setCurrentStep(step);
    setProgressMessage(message);
  };

  const uploadSingleImage = async (file, parentId, token) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('parentId', parentId);

    const response = await fetch('/api/admin-upload-image', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: fd,
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || t('admin.errorUploadImage', { name: file.name }));
    }

    return response.json();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');
    setShowProgress(true);
    setCurrentStep(PROGRESS_STEPS.FOLDER);

    try {
      if (!accessToken) {
        throw new Error(t('admin.errorNoToken'));
      }

      if (!driveFolderId) {
        throw new Error(t('admin.errorFolderId'));
      }

      const districtName = formData.districtVi.trim();
      const propertyName = formData.titleVi.trim();

      if (!districtName) {
        throw new Error(t('admin.errorDistrictRequired'));
      }
      if (!propertyName) {
        throw new Error(t('admin.errorTitleRequired'));
      }

      // Step 1: Find existing district folder by name, or create a new one
      updateProgress(PROGRESS_STEPS.FOLDER, t('admin.progressFolder'));
      let districtFolderId = null;
      const existingDistrict = folders.find(
        f => f.name.toLowerCase() === districtName.toLowerCase()
      );

      if (existingDistrict) {
        districtFolderId = existingDistrict.id;
      } else {
        const createDistrictRes = await fetch('/api/admin-create-folder', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            parentId: driveFolderId,
            name: districtName,
          }),
        });

        if (!createDistrictRes.ok) {
          throw new Error(t('admin.errorCreateDistrict'));
        }

        const districtData = await createDistrictRes.json();
        districtFolderId = districtData.id;
        loadRootFoldersWithToken(driveFolderId, accessToken);
      }

      // Step 2: Find existing property folder by name inside the district, or create a new one
      updateProgress(PROGRESS_STEPS.LIST, t('admin.progressList'));
      let targetFolderId, targetFolderName;

      const propertyFoldersRes = await fetch(
        `/api/admin-list-folders?folderId=${encodeURIComponent(districtFolderId)}`,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      if (!propertyFoldersRes.ok) {
        throw new Error(t('admin.errorListProperty'));
      }

      const propertyFoldersData = await propertyFoldersRes.json();
      const existingPropertyFolder = (propertyFoldersData.folders || []).find(
        f => f.name.toLowerCase() === propertyName.toLowerCase()
      );

      if (existingPropertyFolder) {
        targetFolderId = existingPropertyFolder.id;
        targetFolderName = existingPropertyFolder.name;
      } else {
        updateProgress(PROGRESS_STEPS.PROPERTY, t('admin.progressProperty'));
        const createPropertyRes = await fetch('/api/admin-create-folder', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            parentId: districtFolderId,
            name: propertyName,
          }),
        });

        if (!createPropertyRes.ok) {
          throw new Error(t('admin.errorCreateProperty'));
        }

        const propertyFolderData = await createPropertyRes.json();
        targetFolderId = propertyFolderData.id;
        targetFolderName = propertyFolderData.name;
      }

      // Step 3: Create property_original.txt (plain text reference) and property.txt (encrypted)
      // Use displayAddress as primary address field; fallback to old address field
      const displayAddressEn = formData.displayAddress || formData.address || formData.addressVi || formData.titleVi;
      const displayAddressVi = formData.displayAddressVi || formData.addressVi || formData.address || formData.titleVi;

      updateProgress(PROGRESS_STEPS.PROPERTY_FILE, t('admin.progressPropertyFile'));
      const propertyContent = `[EN]
title: ${formData.title || formData.titleVi}
description: ${JSON.stringify(formData.description || formData.descriptionVi)}
displayAddress: ${displayAddressEn}
city: ${formData.city || formData.cityVi}
district: ${formData.district || formData.districtVi}
type: ${formData.propertyType}
price: ${formData.price}
phone: ${formData.phone}
email: ${formData.email}
available: ${formData.available}
shortTermLease: ${formData.shortTermLease}
amenities: ${formData.amenities.join(', ')}
nearby: ${formData.nearby || formData.nearbyVi}
additionalAddress1: ${formData.additionalAddress1 || formData.additionalAddress1Vi || ''}
additionalAddress2: ${formData.additionalAddress2 || formData.additionalAddress2Vi || ''}

[VI]
title: ${formData.titleVi}
description: ${JSON.stringify(formData.descriptionVi)}
displayAddress: ${displayAddressVi}
city: ${formData.cityVi}
district: ${formData.districtVi}
type: ${formData.propertyType}
price: ${formData.price}
phone: ${formData.phone}
email: ${formData.email}
available: ${formData.available}
shortTermLease: ${formData.shortTermLease}
amenities: ${formData.amenities.join(', ')}
nearby: ${formData.nearbyVi}
additionalAddress1: ${formData.additionalAddress1Vi || ''}
additionalAddress2: ${formData.additionalAddress2Vi || ''}
`;

      // Create property_original.txt - the plain text reference copy
      const originalFileResponse = await fetch('/api/admin-create-file', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          parentId: targetFolderId,
          fileName: 'property_original.txt',
          content: propertyContent,
        }),
      });

      if (!originalFileResponse.ok) {
        throw new Error(t('admin.errorCreateFile'));
      }

      // Create property.txt - server-side encrypted with XOR cipher + base64 encoded
      const encryptedFileResponse = await fetch('/api/admin-create-file', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          parentId: targetFolderId,
          fileName: 'property.txt',
          content: propertyContent,
          encrypt: true,
        }),
      });

      if (!encryptedFileResponse.ok) {
        throw new Error(t('admin.errorCreateFile'));
      }

      // Step 4: Create secret.txt if secret info was provided
      if (formData.secretInfo.trim()) {
        updateProgress(PROGRESS_STEPS.SECRET_FILE, t('admin.progressSecretFile'));
        const secretContent = formData.secretInfo.trim();

        const secretFileResponse = await fetch('/api/admin-create-file', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            parentId: targetFolderId,
            fileName: 'secret.txt',
            content: secretContent,
          }),
        });

        if (!secretFileResponse.ok) {
          throw new Error(t('admin.errorCreateFile'));
        }

        const secretFileData = await secretFileResponse.json();
        try {
          const permsRes = await fetch(
            `https://www.googleapis.com/drive/v3/files/${secretFileData.id}/permissions?fields=permissions(id,role)`,
            {
              headers: { 'Authorization': `Bearer ${accessToken}` },
            }
          );
          if (permsRes.ok) {
            const permsData = await permsRes.json();
            const permissions = permsData.permissions || [];
            for (const perm of permissions) {
              if (perm.role !== 'owner') {
                await fetch(
                  `https://www.googleapis.com/drive/v3/files/${secretFileData.id}/permissions/${perm.id}`,
                  {
                    method: 'DELETE',
                    headers: { 'Authorization': `Bearer ${accessToken}` },
                  }
                );
              }
            }
          }
        } catch (permErr) {
          console.warn('Could not remove public permissions from secret.txt', permErr);
        }
      }

      // Step 5: Upload images sequentially and collect their IDs
      let uploadedCount = 0;
      const uploadedImages = [];
      if (selectedFiles.length > 0) {
        setTotalUploads(selectedFiles.length);
        updateProgress(PROGRESS_STEPS.IMAGES, t('admin.progressImages', { count: `0/${selectedFiles.length}` }));

        for (let i = 0; i < selectedFiles.length; i++) {
          const file = selectedFiles[i];
          const result = await uploadSingleImage(file, targetFolderId, accessToken);
          uploadedImages.push({ id: result.id, name: file.name });
          uploadedCount = i + 1;
          updateProgress(PROGRESS_STEPS.IMAGES, t('admin.progressImages', { count: `${uploadedCount}/${selectedFiles.length}` }));
          setUploadProgress(Math.round(((i + 1) / selectedFiles.length) * 100));
        }
      }

      // Collect all addresses for the additionalAddresses array
      const addrs = [];
      if (formData.additionalAddress1Vi || formData.additionalAddress1) addrs.push(formData.additionalAddress1Vi || formData.additionalAddress1);
      if (formData.additionalAddress2Vi || formData.additionalAddress2) addrs.push(formData.additionalAddress2Vi || formData.additionalAddress2);
      // Also include display address for geocoding
      if (displayAddressVi) addrs.unshift(displayAddressVi);

      // Sync to D1 cache with uploaded image IDs
      try {
        await fetch('/api/admin-sync-property', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: targetFolderId,
            name: propertyName,
            districtName: districtName,
            imageFiles: uploadedImages,
            titleVi: formData.titleVi,
            titleEn: formData.title || formData.titleVi,
            descriptionVi: formData.descriptionVi,
            descriptionEn: formData.description || formData.descriptionVi,
            displayAddressVi: displayAddressVi,
            displayAddressEn: displayAddressEn,
            additionalAddress1: formData.additionalAddress1Vi || formData.additionalAddress1 || '',
            additionalAddress1En: formData.additionalAddress1 || '',
            additionalAddress2: formData.additionalAddress2Vi || formData.additionalAddress2 || '',
            additionalAddress2En: formData.additionalAddress2 || '',
            additionalAddresses: addrs,
            addressVi: displayAddressVi,
            addressEn: displayAddressEn,
            cityVi: formData.cityVi,
            cityEn: formData.city,
            districtVi: districtName,
            districtEn: formData.district || districtName,
            price: parseInt(formData.price) || 0,
            phone: formData.phone,
            email: formData.email,
            propertyType: formData.propertyType,
            amenities: formData.amenities,
            available: formData.available,
            shortTermLease: formData.shortTermLease,
            nearbyVi: formData.nearbyVi ? formData.nearbyVi.split(',').map(s => s.trim()) : [],
            nearbyEn: formData.nearby ? formData.nearby.split(',').map(s => s.trim()) : [],
          }),
        });
        console.log('[AdminPage] Synced to D1 cache');
      } catch (syncErr) {
        console.warn('[AdminPage] Failed to sync to D1 cache:', syncErr);
      }

      // Done
      updateProgress(PROGRESS_STEPS.DONE, t('admin.progressDone'));
      await new Promise(resolve => setTimeout(resolve, 800));

      clearAllCaches();

      let successMsg = t('admin.successMsg', { name: targetFolderName });
      if (uploadedCount > 0) {
        successMsg += ' | ' + t('admin.uploadSuccess', { count: uploadedCount });
      }
      
      setSuccessPopupMessage(successMsg);
      setShowSuccessPopup(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      
      setFormData({
        title: '',
        titleVi: '',
        description: '',
        descriptionVi: '',
        address: '',
        addressVi: '',
        displayAddress: '',
        displayAddressVi: '',
        additionalAddress1: '',
        additionalAddress1Vi: '',
        additionalAddress2: '',
        additionalAddress2Vi: '',
        city: 'Ho Chi Minh City',
        cityVi: 'Hồ Chí Minh',
        district: '',
        districtVi: '',
        price: '',
        phone: '',
        email: '',
        available: true,
        propertyType: 'Phòng (1 - 2 người)',
        amenities: [],
        nearby: '',
        nearbyVi: '',
        secretInfo: '',
      });
      setSelectedFiles([]);

    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
      setShowProgress(false);
      setCurrentStep('');
      setUploadProgress(0);
      setTotalUploads(0);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4">
        <AdminBreadcrumb />
        
        <div className="bg-white rounded-xl shadow-lg p-6 md:p-8">
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-3xl font-bold text-gray-900">{t('admin.title')}</h1>
            <div className="flex items-center gap-2">
              <span className={`flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full font-medium ${
                tokenStatus === 'valid' 
                  ? 'bg-green-50 text-green-700 border border-green-200' 
                  : tokenStatus === 'expired'
                    ? 'bg-red-50 text-red-700 border border-red-200'
                    : 'bg-gray-50 text-gray-500 border border-gray-200'
              }`}>
                <span className={`w-1.5 h-1.5 rounded-full ${
                  tokenStatus === 'valid' ? 'bg-green-500' 
                  : tokenStatus === 'expired' ? 'bg-red-500' 
                  : 'bg-gray-400'
                }`} />
                {tokenStatus === 'valid' ? t('admin.tokenValid') 
                  : tokenStatus === 'expired' ? t('admin.tokenExpired') 
                  : ''}
              </span>
              <button
                onClick={handleRefreshToken}
                disabled={tokenRefreshing || !accessToken}
                className={`p-2 rounded-lg transition-all duration-200 ${
                  tokenRefreshing 
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                    : 'text-gray-600 hover:text-primary-600 hover:bg-primary-50'
                }`}
                title={t('admin.refreshToken')}
              >
                {tokenRefreshing ? (
                  <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                ) : (
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                )}
              </button>
            </div>
          </div>

          {error && (
            <div className="mb-6 bg-red-50 border border-red-200 text-red-700 rounded-lg p-4">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">{t('admin.propertyDetails')}</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('admin.titleVi')}</label>
                  <input type="text" name="titleVi" value={formData.titleVi} onChange={handleInputChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('admin.titleEn')}</label>
                  <input type="text" name="title" value={formData.title} onChange={handleInputChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('admin.descriptionVi')}</label>
                <textarea name="descriptionVi" value={formData.descriptionVi} onChange={handleInputChange} rows="4" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" required />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('admin.descriptionEn')}</label>
                <textarea name="description" value={formData.description} onChange={handleInputChange} rows="3" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" />
              </div>

              {/* Display Address (VI/EN) - renamed from address */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('admin.displayAddressVi')}</label>
                  <input type="text" name="displayAddressVi" value={formData.displayAddressVi} onChange={handleInputChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('admin.displayAddressEn')}</label>
                  <input type="text" name="displayAddress" value={formData.displayAddress} onChange={handleInputChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" />
                </div>
              </div>

              {/* Additional Address 1 (VI/EN) */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('admin.additionalAddress1Vi')}</label>
                  <input type="text" name="additionalAddress1Vi" value={formData.additionalAddress1Vi} onChange={handleInputChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('admin.additionalAddress1En')}</label>
                  <input type="text" name="additionalAddress1" value={formData.additionalAddress1} onChange={handleInputChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" />
                </div>
              </div>

              {/* Additional Address 2 (VI/EN) */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('admin.additionalAddress2Vi')}</label>
                  <input type="text" name="additionalAddress2Vi" value={formData.additionalAddress2Vi} onChange={handleInputChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('admin.additionalAddress2En')}</label>
                  <input type="text" name="additionalAddress2" value={formData.additionalAddress2} onChange={handleInputChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('admin.cityVi')}</label>
                  <input type="text" name="cityVi" value={formData.cityVi} onChange={handleInputChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('admin.cityEn')}</label>
                  <input type="text" name="city" value={formData.city} onChange={handleInputChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('admin.districtVi')}</label>
                  <select name="districtVi" value={formData.districtVi} onChange={handleInputChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" required>
                    <option value="">{t('admin.selectDistrict')}</option>
                    {DISTRICT_KEYS.map(key => (
                      <option key={key} value={t(`about.form.${key}`)}>{t(`about.form.${key}`)}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('admin.price')}</label>
                  <input type="number" name="price" value={formData.price} onChange={handleInputChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" placeholder={t('admin.pricePlaceholder')} required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('admin.propertyType')}</label>
                  <select name="propertyType" value={formData.propertyType} onChange={handleInputChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" required>
                    {PROPERTY_TYPES.map(type => (
                      <option key={type.value} value={type.value}>{t(type.labelKey)}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('admin.phone')}</label>
                  <input type="tel" name="phone" value={formData.phone} onChange={handleInputChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('admin.email')}</label>
                  <input type="email" name="email" value={formData.email} onChange={handleInputChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" />
                </div>
              </div>

              <div className="flex items-center gap-6">
                <label className="flex items-center gap-2">
                  <input type="checkbox" name="available" checked={formData.available} onChange={handleInputChange} className="w-4 h-4 text-primary-600 rounded" />
                  <span className="text-sm font-medium text-gray-700">{t('admin.available')}</span>
                </label>
                <label className="flex items-center gap-2">
                  <input type="checkbox" name="shortTermLease" checked={formData.shortTermLease} onChange={handleInputChange} className="w-4 h-4 text-primary-600 rounded" />
                  <span className="text-sm font-medium text-gray-700">{t('admin.shortTermLease')}</span>
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('admin.amenities')}</label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {AMENITIES_LIST.map(amenity => (
                    <label key={amenity.key} className="flex items-center gap-2 p-2 border rounded-lg cursor-pointer hover:bg-gray-50">
                      <input type="checkbox" checked={formData.amenities.includes(amenity.key)} onChange={() => handleAmenityToggle(amenity.key)} className="w-4 h-4 text-primary-600 rounded" />
                      <span className="text-sm">{amenity.icon} {t(`amenity.${amenity.key}`, amenity.key)}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('admin.nearbyVi')}</label>
                <input type="text" name="nearbyVi" value={formData.nearbyVi} onChange={handleInputChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" placeholder={t('admin.nearbyPlaceholder')} />
              </div>

              {/* Image Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('admin.images')}</label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*,video/*"
                    multiple
                    onChange={handleFileChange}
                    className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100"
                  />
                  <p className="mt-1 text-xs text-gray-500">{t('admin.imagesHelp')}</p>
                  {selectedFiles.length > 0 && (
                    <div className="mt-3 space-y-1">
                      {selectedFiles.map((file, index) => (
                        <div key={index} className="flex items-center justify-between bg-gray-50 rounded px-3 py-1.5">
                          <span className="text-sm text-gray-700 truncate max-w-[300px]">{file.type.startsWith('video/') ? '🎬' : '🖼️'} {file.name}</span>
                          <button type="button" onClick={() => removeFile(index)} className="text-red-500 hover:text-red-700 text-sm ml-2">✕</button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Secret Info */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('admin.secretInfo')}</label>
                <textarea name="secretInfo" value={formData.secretInfo} onChange={handleInputChange} rows="3" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" placeholder={t('admin.secretInfoPlaceholder')} />
              </div>
            </div>

            <div className="flex gap-4 pt-4">
              <button type="submit" disabled={loading} className="flex-1 bg-primary-600 text-white py-3 px-6 rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                {loading ? t('admin.creating') : t('admin.create')}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Animated Progress Overlay */}
      {showProgress && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl p-8 max-w-sm w-full mx-4">
            <div className="text-center">
              <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('admin.progressTitle')}</h3>
              <div className="space-y-4">
                {STEP_ORDER.map((step) => {
                  const stepIndex = STEP_ORDER.indexOf(step);
                  const currentIndex = STEP_ORDER.indexOf(currentStep);
                  const isActive = step === currentStep;
                  const isCompleted = stepIndex < currentIndex;

                  let stepIcon = (
                    <div className="w-6 h-6 rounded-full border-2 border-gray-300 flex items-center justify-center">
                      <div className="w-2 h-2 rounded-full bg-gray-300" />
                    </div>
                  );

                  if (isActive) {
                    stepIcon = (
                      <div className="w-6 h-6 rounded-full border-2 border-primary-500 border-t-transparent animate-spin" />
                    );
                  } else if (isCompleted) {
                    stepIcon = (
                      <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center">
                        <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                    );
                  }

                  let message;
                  if (step === PROGRESS_STEPS.FOLDER) {
                    message = t('admin.progressFolder');
                  } else if (step === PROGRESS_STEPS.LIST) {
                    message = t('admin.progressList');
                  } else if (step === PROGRESS_STEPS.PROPERTY) {
                    message = t('admin.progressProperty');
                  } else if (step === PROGRESS_STEPS.PROPERTY_FILE) {
                    message = t('admin.progressPropertyFile');
                  } else if (step === PROGRESS_STEPS.SECRET_FILE) {
                    message = t('admin.progressSecretFile');
                  } else if (step === PROGRESS_STEPS.IMAGES) {
                    message = isActive ? progressMessage : t('admin.progressImages', { count: `${totalUploads}/${totalUploads}` });
                  } else if (step === PROGRESS_STEPS.DONE) {
                    message = isActive || isCompleted ? t('admin.progressDone') : '';
                  }

                  return (
                    <div key={step} className="flex items-center gap-3">
                      <div className="flex-shrink-0">{stepIcon}</div>
                      <div className="flex-1 text-left">
                        <span className={`text-sm ${isActive ? 'text-primary-600 font-medium' : isCompleted ? 'text-green-600' : 'text-gray-400'}`}>
                          {message}
                        </span>
                        {step === PROGRESS_STEPS.IMAGES && isActive && totalUploads > 0 && (
                          <div className="mt-1.5 w-full bg-gray-200 rounded-full h-1.5">
                            <div className="bg-primary-500 h-1.5 rounded-full transition-all duration-300" style={{ width: `${uploadProgress}%` }} />
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Success Popup */}
      {showSuccessPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl p-8 max-w-md w-full mx-4 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">✅ {t('admin.progressDone')}</h3>
            <p className="text-gray-600 mb-6">{successPopupMessage}</p>
            <div className="flex gap-3 justify-center">
              <button
                onClick={() => {
                  setShowSuccessPopup(false);
                  setSuccess('');
                  navigate('/admin');
                }}
                className="bg-primary-600 text-white px-6 py-2.5 rounded-lg hover:bg-primary-700 transition-colors font-medium"
              >
                {t('admin.backToHome')}
              </button>
              <button
                onClick={() => {
                  setShowSuccessPopup(false);
                  setSuccess('');
                }}
                className="px-6 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium"
              >
                {t('admin.create')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPage;